import CircularProgress from "@mui/material/CircularProgress";

import { datadogLogs } from "@datadog/browser-logs";
import { useDebounceFn } from "ahooks";
import { useAtom, useAtomValue } from "jotai";
import {
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useResizeObserver } from "usehooks-ts";
import {
  activeWaveformQueryKeyAtom,
  currentTimeAtom,
  mediaElementAtom,
} from "../../../../atoms/audioAtom";
import { useAddStyleToShadowRoot } from "../../../../hooks/audioPlayerHooks/useAddStyleToShadowRoot";
import { useSetupLockScreenControls } from "../../../../hooks/audioPlayerHooks/useSetupLockScreenControls";
import { useSetupOnClick } from "../../../../hooks/audioPlayerHooks/UseSetupOnClick";
import {
  FOOTER_WAVESURFER_REF_ID,
  useGenerateWaveformForFooter,
} from "../../../../hooks/audioPlayerHooks/waveformHooks";
import {
  convertWaveformDurationToReadableTime,
  useSetWaveSurferSeekTime,
} from "../../../../hooks/audioPlayerHooks/waveformUtils";
import {
  setFooterIsActive,
  setIsFooterPlaying,
} from "../../../../store/actions/abPlayerStore";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  getCurrentTrackBlobUrlIsLoading,
  getCurrentTrackDownloadedProjectBlob,
  getCurrentTrackDownloadedProjectBlobMp3s,
} from "../../../../store/selectors/abPlayerSelectors";
import { Text } from "../../../core-ui/components/Text/Text";
import {
  TextColor,
  TextStyleVariant,
} from "../../../core-ui/components/Text/TextUtils";
import { Size } from "../../SessionScheduleGuide/SessionScheduleGuide";
import {
  StyledFooterWaveformContainer,
  StyledFooterWaveformP,
  StyledWaveform,
  StyledWaveformLoadingLosslessAudioContainer,
} from "./FooterWaveform.styles";
import { useQueryClient } from "@tanstack/react-query";

export const FooterWaveform = () => {
  const footerWaveformParentRef = useRef<HTMLDivElement>(null);
  const { waveformRef, currentSeekTime, duration } =
    useGenerateWaveformForFooter();
  const bloblUrlMp3 = useAppSelector(
    getCurrentTrackDownloadedProjectBlobMp3s(),
  );
  const { queryKey: activeWaveformQueryKey } = useAtomValue(
    activeWaveformQueryKeyAtom,
  );
  const queryClient = useQueryClient();
  const data = queryClient.getQueryData<{
    url: string;
    content_type: string;
    waveform_peaks?: string;
    duration?: number;
  }>(activeWaveformQueryKey);
  const url = data?.url;

  const { showFooterPlayer } = useAppSelector((state) => state.abPlayerStore);
  const blobUrl = useAppSelector(getCurrentTrackDownloadedProjectBlob());
  const isCurrentTrackBlobUrlIsLoading = useAppSelector(
    getCurrentTrackBlobUrlIsLoading(),
  );
  const dispatch = useAppDispatch();
  const { playOnLoad } = useAppSelector((state) => state.abPlayerStore);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const stopBubble: MouseEventHandler<HTMLDivElement> = useCallback((e) => {
    e.stopPropagation();
  }, []);
  const [audioElement, setMediaElement] = useAtom(mediaElementAtom);
  const [currentPosition, setCurrentPosition] = useAtom(currentTimeAtom);

  const [width, setWidth] = useState(0);
  const onResize = useCallback(
    (size: Size) => {
      setTimeout(() => {
        setWidth(size.width || 0);
      }, 0);
    },
    [setWidth],
  );
  useResizeObserver({
    ref: footerWaveformParentRef,
    box: "border-box",
    onResize,
  });

  useSetupLockScreenControls();

  useAddStyleToShadowRoot(
    FOOTER_WAVESURFER_REF_ID,
    width ? `${width}px` : undefined,
  );

  const handleAudio = () => {
    if (!audioElement || audioElement.paused) {
      return;
    }
    setCurrentPosition(audioElement.currentTime);
    currentSeekTime.current = convertWaveformDurationToReadableTime(
      audioElement.currentTime,
    );
  };

  const { run: throttledHandleAudio } = useDebounceFn(handleAudio, {
    wait: 500,
    maxWait: 500,
  });

  useEffect(() => {
    const handlePlayEvent = () => {
      dispatch(setIsFooterPlaying(true));
      if ("mediaSession" in navigator) {
        navigator.mediaSession.playbackState = "playing";
      }
    };

    const handlePauseEvent = () => {
      dispatch(setIsFooterPlaying(false));
      if ("mediaSession" in navigator) {
        navigator.mediaSession.playbackState = "paused";
      }
    };

    const onCanPlay = () => {
      dispatch(setFooterIsActive(true));
    };

    if (!url) {
      return;
    }
    if (!audioElement) {
      const audio = new Audio(url);
      audio.style.position = "absolute";
      audio.style.zIndex = "-1";
      audio.style.display = "none";
      audio.currentTime = currentPosition;
      audio.addEventListener("canplay", onCanPlay);
      audio.addEventListener("play", handlePlayEvent);
      audio.addEventListener("pause", handlePauseEvent);
      audio.addEventListener("timeupdate", () => throttledHandleAudio());
      setMediaElement(audio);
      audioRef.current = audio;
    }

    if (!audioRef.current) {
      return;
    }

    audioRef.current.src = url;
    audioRef.current.preload = "metadata";
    audioRef.current.load();
    audioRef.current.currentTime = currentPosition;

    if (playOnLoad) {
      try {
        void audioRef.current.play();
      } catch (error) {
        datadogLogs.logger.error("failed to play", {
          name: "html media element",
          url: url,
          error: error,
        });
      }
    }

    // Cleanup function
    return () => {
      if (!audioElement) {
        return;
      }
      audioElement.pause();
      audioElement.removeEventListener("canplay", onCanPlay);
      audioElement.removeEventListener("play", handlePlayEvent);
      audioElement.removeEventListener("pause", handlePauseEvent);
      audioElement.removeEventListener("timeupdate", () =>
        throttledHandleAudio(),
      );
      audioElement.remove();
    };
  }, [
    url,
    showFooterPlayer,
    playOnLoad,
    audioElement,
    setMediaElement,
    throttledHandleAudio,
    dispatch,
  ]);

  useEffect(() => {
    if (!showFooterPlayer && audioElement) {
      audioElement.pause();
      audioElement.remove();
    }
  }, [audioElement, showFooterPlayer]);

  useSetupOnClick(waveformRef, !audioElement);

  const setSeekTime = useSetWaveSurferSeekTime(
    currentPosition,
    waveformRef?.current,
  );

  useEffect(() => {
    if (!waveformRef.current) {
      return;
    }
    if (currentPosition < 0) {
      return;
    }
    setSeekTime();
  }, [currentPosition, setSeekTime, waveformRef]);

  return (
    <StyledFooterWaveformContainer onClick={stopBubble}>
      {waveformRef.current && (
        <StyledFooterWaveformP>{currentSeekTime.current}</StyledFooterWaveformP>
      )}
      <StyledWaveform
        ref={footerWaveformParentRef}
        id={FOOTER_WAVESURFER_REF_ID}
      />

      {waveformRef.current && (
        <StyledFooterWaveformP>{duration}</StyledFooterWaveformP>
      )}
      {url === bloblUrlMp3 && (
        <StyledWaveformLoadingLosslessAudioContainer>
          {" "}
          {isCurrentTrackBlobUrlIsLoading ? (
            <>
              <Text variant={TextStyleVariant.P1} color={TextColor.WHITE}>
                Loading Lossless Audio
              </Text>
              <CircularProgress
                size={16}
                className="lossless-audio-loading-circle"
              />
            </>
          ) : blobUrl === null ? (
            <Text variant={TextStyleVariant.P1} color={TextColor.WHITE}>
              Could not load lossless audio. Playing MP3 version
            </Text>
          ) : null}
        </StyledWaveformLoadingLosslessAudioContainer>
      )}
    </StyledFooterWaveformContainer>
  );
};
